/**
 * ProjectDetailPage - displays detailed view of a single project
 * Route: /projects/:projectId
 */
import { useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import {
  ArrowLeft,
  Edit2,
  Trash2,
  Plus,
  GitBranch,
  ExternalLink,
  MessageSquare,
  Activity,
  AlertCircle,
  Bot,
  BookOpen
} from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import {
  ProjectArchivedBanner,
  ProjectDetailSkeleton
} from '@/components/projects'
import { SessionStatusBadge } from '@/components/sessions/SessionStatusBadge'
import { ConstraintsInput } from '@/components/forms/ConstraintsInput'
import { ContextItemTypeIcon, getTypeLabel } from '@/components/chatbot/ContextItemTypeIcon'
import { useProject, useDeleteProject } from '@/api/projects'
import { useSessions } from '@/api/orchestrator'
import { createChatbotSession } from '@/api/chatbot'
import { usePermissions } from '@/hooks/usePermissions'
import { cn } from '@/lib/utils'

/**
 * Project detail page showing project information, constraints, and sessions.
 */
export function ProjectDetailPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [isCreatingConversation, setIsCreatingConversation] = useState(false)

  // Permission checks
  const { hasPermission } = usePermissions()
  const canEditProject = hasPermission('project:update')
  const canDeleteProject = hasPermission('project:delete')

  // Fetch project details
  const {
    data: project,
    isLoading: projectLoading,
    error: projectError
  } = useProject(projectId!)

  // Fetch background sessions (orchestration tasks)
  const {
    data: sessionsData,
    isLoading: sessionsLoading
  } = useSessions({ projectId, mode: 'Background' })

  // Fetch chatbot conversations
  const {
    data: conversationsData,
    isLoading: conversationsLoading
  } = useSessions({ projectId, mode: 'Chatbot' })

  // Delete mutation
  const deleteProject = useDeleteProject()

  const handleStartConversation = async () => {
    if (!projectId) return

    setIsCreatingConversation(true)
    try {
      const response = await createChatbotSession({ projectId })
      navigate(`/assistant?session=${response.sessionId}`)
      toast.success('Conversation created')
    } catch (error) {
      toast.error('Failed to create conversation')
    } finally {
      setIsCreatingConversation(false)
    }
  }

  const handleDeleteConfirm = async () => {
    if (!projectId) return

    try {
      await deleteProject.mutateAsync({
        projectId,
        hardDelete: false
      })
      navigate('/projects')
    } catch (error) {
      setDeleteError(
        error instanceof Error
          ? error.message
          : 'Failed to archive project'
      )
      setShowDeleteDialog(false)
    }
  }

  // Loading state
  if (projectLoading) {
    return (
      <PageShell>
        <div className="p-6 space-y-6" data-testid="project-detail-skeleton">
          <ProjectDetailSkeleton />
        </div>
      </PageShell>
    )
  }

  // Error state
  if (projectError || !project) {
    return (
      <PageShell>
        <div className="p-6 space-y-6">
          <Alert variant="destructive">
            <AlertTitle>Project Not Found</AlertTitle>
            <AlertDescription>
              The project you're looking for doesn't exist or you don't have access to it.
            </AlertDescription>
          </Alert>
          <Button asChild className="mt-4">
            <Link to="/projects">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Projects
            </Link>
          </Button>
        </div>
      </PageShell>
    )
  }

  const sessions = sessionsData?.sessions ?? []
  const conversations = conversationsData?.sessions ?? []

  return (
    <PageShell>
      <div className="p-6 space-y-6">
        {/* Archived Banner */}
        {project.isArchived && (
          <ProjectArchivedBanner
            variant="warning"
            message="This project is archived and its sessions cannot be modified."
          />
        )}

        {/* Delete Error */}
        {deleteError && (
          <Alert variant="destructive">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{deleteError}</AlertDescription>
          </Alert>
        )}

        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <Button variant="ghost" size="sm" asChild className="-ml-2">
                <Link to="/projects">
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Projects
                </Link>
              </Button>
            </div>
            <h1 className={cn(
              'text-2xl font-bold truncate',
              project.isArchived && 'text-muted-foreground'
            )}>
              {project.name}
            </h1>
            {project.description && (
              <p className="text-muted-foreground mt-1">{project.description}</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 shrink-0">
            {canEditProject && (
              <Button variant="outline" asChild>
                <Link to={`/projects/${projectId}/edit`}>
                  <Edit2 className="h-4 w-4 mr-2" />
                  Edit
                </Link>
              </Button>
            )}
            {canDeleteProject && (
              <Button
                variant="outline"
                onClick={() => setShowDeleteDialog(true)}
                disabled={project.isArchived}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Archive
              </Button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardContent className="flex items-center gap-3 p-4">
              <div className="rounded-full bg-blue-100 p-2 dark:bg-blue-950">
                <MessageSquare className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{project.sessionCount ?? 0}</p>
                <p className="text-sm text-muted-foreground">
                  {project.sessionCount === 1 ? 'session' : 'sessions'}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-3 p-4">
              <div className="rounded-full bg-green-100 p-2 dark:bg-green-950">
                <Activity className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{project.activeSessionCount ?? 0}</p>
                <p className="text-sm text-muted-foreground">active</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-3 p-4">
              <div className="rounded-full bg-red-100 p-2 dark:bg-red-950">
                <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{project.failedSessionCount ?? 0}</p>
                <p className="text-sm text-muted-foreground">failed</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex flex-col gap-2">
              <Button asChild className="w-full">
                <Link to={`/orchestrate?projectId=${projectId}`}>
                  <Plus className="h-4 w-4 mr-2" />
                  New Session
                </Link>
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={handleStartConversation}
                disabled={project.isArchived || isCreatingConversation}
              >
                <Bot className="h-4 w-4 mr-2" />
                {isCreatingConversation ? 'Creating...' : 'New Conversation'}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Repository Info */}
        {(project.defaultRepository || project.defaultBranch) && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Repository</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {project.defaultRepository && (
                <div className="flex items-center gap-2 text-sm">
                  <ExternalLink className="h-4 w-4 text-muted-foreground" />
                  <a
                    href={project.defaultRepository}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline dark:text-blue-400"
                  >
                    {project.defaultRepository.replace(/^https?:\/\//, '')}
                  </a>
                </div>
              )}
              {project.defaultBranch && (
                <div className="flex items-center gap-2 text-sm">
                  <GitBranch className="h-4 w-4 text-muted-foreground" />
                  <Badge variant="secondary">{project.defaultBranch}</Badge>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Constraints */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Constraints</CardTitle>
          </CardHeader>
          <CardContent>
            {(project.constraints ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No constraints defined.</p>
            ) : (
              <ConstraintsInput
                value={project.constraints ?? []}
                onChange={() => {}}
                readOnly={true}
                showModeToggle={false}
                showTemplates={false}
              />
            )}
          </CardContent>
        </Card>

        {/* Knowledge Base */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              Knowledge Base
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(project.contextItems ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No knowledge base items defined.</p>
            ) : (
              <ul className="space-y-2" role="list" aria-label="Knowledge base items">
                {(project.contextItems ?? []).map((item, index) => (
                  <li
                    key={`${item.type}-${item.value}-${index}`}
                    className="flex items-start gap-3 p-3 rounded-md bg-muted/50 border"
                  >
                    <ContextItemTypeIcon type={item.type} className="mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-muted-foreground uppercase">
                          {getTypeLabel(item.type)}
                        </span>
                      </div>
                      <p className="text-sm font-medium break-all">{item.value}</p>
                      {item.note && (
                        <p className="text-xs text-muted-foreground mt-1">{item.note}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Context */}
        {project.contextTemplate && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Context</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                {project.contextTemplate}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Sessions & Conversations Tabs */}
        <Card>
          <CardHeader className="pb-2">
            <Tabs defaultValue="sessions" className="w-full">
              <div className="flex items-center justify-between">
                <TabsList>
                  <TabsTrigger value="sessions" className="gap-2">
                    <MessageSquare className="h-4 w-4" />
                    Sessions
                    {sessions.length > 0 && (
                      <Badge variant="secondary" className="ml-1">{sessions.length}</Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="conversations" className="gap-2">
                    <Bot className="h-4 w-4" />
                    Conversations
                    {conversations.length > 0 && (
                      <Badge variant="secondary" className="ml-1">{conversations.length}</Badge>
                    )}
                  </TabsTrigger>
                </TabsList>
              </div>

              {/* Sessions Tab Content */}
              <TabsContent value="sessions" className="mt-4">
                {sessionsLoading ? (
                  <div className="space-y-2">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="h-12 bg-muted animate-pulse rounded-md" />
                    ))}
                  </div>
                ) : sessions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>No sessions yet.</p>
                    <Button asChild variant="link" className="mt-2">
                      <Link to={`/orchestrate?projectId=${projectId}`}>
                        <Plus className="h-4 w-4 mr-2" />
                        Create your first session
                      </Link>
                    </Button>
                  </div>
                ) : (
                  <>
                    <ul className="space-y-2" role="list" aria-label="Sessions">
                      {sessions.slice(0, 10).map((session) => (
                        <li key={session.sessionId}>
                          <Link
                            to={`/sessions/${session.sessionId}`}
                            className="flex items-center justify-between p-3 rounded-md border hover:bg-accent transition-colors"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <MessageSquare className="h-4 w-4 text-muted-foreground shrink-0" />
                              <span className="font-medium truncate">
                                {session.lastUserMessage || `Session ${session.sessionId.slice(0, 8)}`}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 shrink-0 text-sm text-muted-foreground">
                              <span>{session.messageCount} messages</span>
                              <SessionStatusBadge status={session.status} size="sm" />
                            </div>
                          </Link>
                        </li>
                      ))}
                    </ul>
                    {sessions.length > 10 && (
                      <div className="text-center pt-2">
                        <Button asChild variant="link">
                          <Link to={`/sessions?projectId=${projectId}`}>
                            View all {sessions.length} sessions
                          </Link>
                        </Button>
                      </div>
                    )}
                    <div className="pt-4 border-t mt-4">
                      <Button asChild variant="outline" size="sm">
                        <Link to={`/orchestrate?projectId=${projectId}`}>
                          <Plus className="h-4 w-4 mr-2" />
                          New Session
                        </Link>
                      </Button>
                    </div>
                  </>
                )}
              </TabsContent>

              {/* Conversations Tab Content */}
              <TabsContent value="conversations" className="mt-4">
                {conversationsLoading ? (
                  <div className="space-y-2">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="h-12 bg-muted animate-pulse rounded-md" />
                    ))}
                  </div>
                ) : conversations.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>No conversations yet.</p>
                    <Button
                      variant="link"
                      className="mt-2"
                      onClick={handleStartConversation}
                      disabled={project.isArchived || isCreatingConversation}
                    >
                      <Bot className="h-4 w-4 mr-2" />
                      {isCreatingConversation ? 'Creating...' : 'Start your first conversation'}
                    </Button>
                  </div>
                ) : (
                  <>
                    <ul className="space-y-2" role="list" aria-label="Conversations">
                      {conversations.slice(0, 10).map((conversation) => (
                        <li key={conversation.sessionId}>
                          <Link
                            to={`/assistant?session=${conversation.sessionId}`}
                            className="flex items-center justify-between p-3 rounded-md border hover:bg-accent transition-colors"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <Bot className="h-4 w-4 text-muted-foreground shrink-0" />
                              <span className="font-medium truncate">
                                {conversation.lastUserMessage || `Conversation ${conversation.sessionId.slice(0, 8)}`}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 shrink-0 text-sm text-muted-foreground">
                              <span>{conversation.messageCount} messages</span>
                              <SessionStatusBadge status={conversation.status} size="sm" />
                            </div>
                          </Link>
                        </li>
                      ))}
                    </ul>
                    {conversations.length > 10 && (
                      <div className="text-center pt-2">
                        <Button asChild variant="link">
                          <Link to={`/sessions?projectId=${projectId}&mode=Chatbot`}>
                            View all {conversations.length} conversations
                          </Link>
                        </Button>
                      </div>
                    )}
                    <div className="pt-4 border-t mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleStartConversation}
                        disabled={project.isArchived || isCreatingConversation}
                      >
                        <Bot className="h-4 w-4 mr-2" />
                        {isCreatingConversation ? 'Creating...' : 'New Conversation'}
                      </Button>
                    </div>
                  </>
                )}
              </TabsContent>
            </Tabs>
          </CardHeader>
        </Card>

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          open={showDeleteDialog}
          onOpenChange={setShowDeleteDialog}
          title="Archive Project?"
          description={`Are you sure you want to archive "${project.name}"? The project and its sessions will no longer appear in lists, but you can restore it later.`}
          confirmLabel="Archive"
          onConfirm={handleDeleteConfirm}
          variant="destructive"
        />
      </div>
    </PageShell>
  )
}
